/// <amd-module name="Dashboards/DashboardTemplates/DashboardTemplatePage" />
import * as React from "react";

import * as Resources from "Dashboards/DashboardTemplates/Resources";

import { getTemplateCardProps, getTemplateData, TemplateCardData, TemplateWidgetDescription } from "TemplateData";

import { Dashboard, Widget, WidgetMetadata, WidgetScope, WidgetTypesResponse } from "Dashboards/Common/Contracts/Dashboard";
import { TeamContext } from "Dashboards/Common/Contracts/TeamContext";
import { IDashboardRestClient } from "Dashboards/Common/RestClient/Dashboard";
import { ISemanticVersionService } from "Dashboards/WidgetContractServices/SemanticVersionService";

import { VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { ClientTheme, IVssThemeService } from "VSS/Platform/Theming";
import { Header, HeaderDescription, TitleSize } from "VSSUI/Header";
import { DashboardTemplateCard } from "./DashboardTemplateCard";
import { DashboardTemplatePreview } from "./DashboardTemplatePreview";
import { createTemplateWidgets } from "./DashboardTemplateUtilities";

export interface DashboardTemplatePageProps {
    onEmptyDashboardClick: () => void;
    refreshDashboard: () => void;
    teamContext: TeamContext;
    dashboard: Pick<Dashboard, "eTag" | "name" | "groupId" | "ownerId" | "dashboardScope" | "description" | "id" | "refreshInterval" | "lastAccessedDate" | "modifiedBy" | "modifiedDate" >;
}

export interface DashboardTemplatePageState {
    theme: ClientTheme;
    dashboard: Dashboard;
    templateType: string;
    allWidgets: Widget[];
    allWidgetDescriptions: TemplateWidgetDescription[];
    currentTemplateWidgets: Widget[];
}

export class DashboardTemplatePage extends VssComponent<DashboardTemplatePageProps, DashboardTemplatePageState> {
    private noneSelected: string = "None";
    private client = this.context.pageContext.getRestClient<IDashboardRestClient>("IDashboardRestClient");

    public state: DashboardTemplatePageState = {
        theme: {
            id: "",
            name: "",
            data: {}
        },
        dashboard: {
            _links: undefined,
            eTag: this.props.dashboard.eTag ? this.props.dashboard.eTag : "",
            name: this.props.dashboard.name,
            groupId: this.props.dashboard.groupId,
            ownerId: this.props.dashboard.ownerId,
            dashboardScope: this.props.dashboard.dashboardScope,
            description: this.props.dashboard.description,
            id: this.props.dashboard.id,
            refreshInterval: this.props.dashboard.refreshInterval,
            position: 0,
            url: "",
            widgets: [],
            lastAccessedDate: this.props.dashboard.lastAccessedDate,
            modifiedBy: this.props.dashboard.modifiedBy,
            modifiedDate: this.props.dashboard.lastAccessedDate
        },
        templateType: this.noneSelected,
        allWidgets: [],
        allWidgetDescriptions: [],
        currentTemplateWidgets: []
    };

    public async componentDidMount() {
        const semanticVersionService = this.context.pageContext.getService<ISemanticVersionService>("ISemanticVersionService");
        const currentTheme = await this.context.pageContext.getService<IVssThemeService>("IVssThemeService").getCurrentTheme();
        const widgetMetadata = await this.client
            .getWidgetTypes(WidgetScope.Project_Team, this.props.teamContext.project)
            .then((response: WidgetTypesResponse) => {
                return {
                    widgetTypes: response.widgetTypes.map((type: WidgetMetadata) => {
                        const widget: Widget = {
                            _links: undefined,
                            allowedSizes: type.allowedSizes,
                            areSettingsBlockedForUser: false,
                            artifactId: "",
                            configurationContributionId: type.configurationContributionId,
                            configurationContributionRelativeId: type.configurationContributionRelativeId,
                            contentUri: type.contentUri,
                            contributionId: type.contributionId,

                            dashboard: this.state.dashboard,
                            eTag: this.state.dashboard.eTag,
                            id: this.state.dashboard.id,
                            isEnabled: type.isEnabled,
                            isNameConfigurable: type.isNameConfigurable,
                            lightboxOptions: type.lightboxOptions,
                            loadingImageUrl: type.loadingImageUrl,
                            name: type.name,
                            position: {
                                row: 0,
                                column: 0
                            },
                            settings: type.defaultSettings,
                            settingsVersion: semanticVersionService.getInitialVersion(),
                            size: type.allowedSizes[0],
                            typeId: type.typeId,
                            url: type.loadingImageUrl
                        };
                        return widget;
                    }),
                    widgetDescriptions: response.widgetTypes.map((type: WidgetMetadata) => {
                        const description: TemplateWidgetDescription = {
                            contributionId: type.contributionId,
                            name: type.name,
                            description: type.description
                        };
                        return description;
                    })
                };
            });
        this.setState({ allWidgets: widgetMetadata.widgetTypes, allWidgetDescriptions: widgetMetadata.widgetDescriptions, theme: currentTheme });
    }

    public render(): JSX.Element {
        return (
            <div>
                <Header title={Resources.DashboardTemplatePage_Title} titleSize={TitleSize.Large}>
                    <HeaderDescription>{Resources.DashboardTemplatePage_Subtitle}</HeaderDescription>
                </Header>
                {this.renderDashboardTemplateCards()}
                <DashboardTemplatePreview
                    onDismissClick={this.onDismissClick}
                    onCreateDashboardTemplateClick={this.onCreateDashboardTemplateClick}
                    theme={this.state.theme}
                    templateType={this.state.templateType}
                    noneSelected={this.noneSelected}
                    widgetDescriptions={this.state.allWidgetDescriptions}
                />
            </div>
        );
    }

    /** Wrapped setState function to avoid setting state in render method */
    private onDismissClick = () => {
        this.setState({ templateType: "None", currentTemplateWidgets: [] });
    };

    /** Display interactive array of available templates as cards */
    private renderDashboardTemplateCards = (): JSX.Element[] => {
        const templateCards = getTemplateCardProps().map(templateCardData => {
            const thumbnail = this.state.theme.isDark ? templateCardData.thumbnailDark : templateCardData.thumbnailLight;
            return (
                <div key={templateCardData.type} tabIndex={0} onKeyPress={() => this.onDashboardTemplateCardClick(templateCardData)}>
                    <DashboardTemplateCard
                        templateID={templateCardData.type}
                        templateTitle={templateCardData.title}
                        templateSubtitle={templateCardData.subtitle}
                        templateThumbnail={getAssetLocation(this.context.pageContext, thumbnail)}
                        // Blank dashboard requires a different onClick function than templates
                        onDashboardTemplateCardClick={() => {
                            this.onDashboardTemplateCardClick(templateCardData);
                        }}
                    />
                </div>
            );
        });
        return templateCards;
    };
    
    /** Handles behavior of different template cards on click */
    private onDashboardTemplateCardClick = (templateCardData: TemplateCardData): void => {
        templateCardData.type === "Blank"
            ? this.props.onEmptyDashboardClick()
            : this.setState({
                templateType: templateCardData.type,
                currentTemplateWidgets: createTemplateWidgets(templateCardData.type, getTemplateData(), this.state.allWidgets)
            });
    };

    /** Handles population of widgets when user confirms template choice*/
    private onCreateDashboardTemplateClick = (): void => {
        const teamContext = this.props.teamContext;
        const dashboard = this.state.dashboard;
        this.client
            .replaceWidgets(this.state.currentTemplateWidgets, teamContext, dashboard.id, dashboard.eTag)
            .then(() => this.props.refreshDashboard())
            .catch(error => {
                console.log(Resources.DashboardTemplatePage_ReplaceWidgets_Error.concat(" ", error));
            });
        this.setState({ templateType: "None", currentTemplateWidgets: [] });
    };
}

VssComponent.register("DashboardTemplatePage", DashboardTemplatePage);
